import { useState, useEffect } from 'react';
import { formatCurrency } from '../../utils/invoiceHelpers';

function RefundModal({ isOpen, onClose, onConfirm, transaction, processing, apiError }) {
  // const [refundType, setRefundType] = useState('partial'); // Unused - will be used for full/partial refunds
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [externalReference, setExternalReference] = useState('');
  const [error, setError] = useState('');

  // Reset form when transaction changes
  useEffect(() => {
    if (transaction) {
      const availableAmount = parseFloat(transaction.amount || 0) - parseFloat(transaction.refundedAmount || transaction.refunded_amount || 0);
      setAmount(availableAmount.toFixed(2)); // Keep as string for input field
      setReason('Requested by Customer'); // Default reason per design
      setExternalReference('');
      setError('');
    }
  }, [transaction]);
  
  // Clear local error when API error changes
  useEffect(() => {
    if (apiError) {
      setError('');
    }
  }, [apiError]);

  if (!isOpen || !transaction) return null;

  const maxRefundAmount = parseFloat(transaction.amount || 0) - parseFloat(transaction.refundedAmount || transaction.refunded_amount || 0);
  
  // Reason options per design document
  const reasonOptions = [
    'Requested by Customer',
    'Duplicate Transaction', 
    'Fraudulent Transaction',
    'Product/Service Issue',
    'Billing Error',
    'Other'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    const refundAmount = parseFloat(amount);
    if (!refundAmount || refundAmount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }
    if (refundAmount > maxRefundAmount) {
      setError(`Amount cannot exceed ${formatCurrency(maxRefundAmount)}`);
      return;
    }

    const refundData = {
      amount: parseFloat(amount),
      reason: reason.trim(),
      notes: externalReference.trim() || null
    };

    onConfirm(refundData);
  };

  const handleClose = () => {
    if (processing) return;
    setAmount('');
    setReason('Requested by Customer');
    setExternalReference('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Refund Payment
            </h3>
            {!processing && (
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Transaction:</strong> {transaction.transactionReference || transaction.id}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Available:</strong> {formatCurrency(maxRefundAmount)}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount: 
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={maxRefundAmount}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={processing}
                  className="w-full border border-gray-300 rounded px-8 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason *
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={processing}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
              >
                {reasonOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={externalReference}
                onChange={(e) => setExternalReference(e.target.value)}
                disabled={processing}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                rows="3"
                placeholder="Additional notes about this refund..."
              />
            </div>

            {(error || apiError) && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error || apiError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={processing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded disabled:opacity-50"
              >
                {processing ? 'Processing...' : `Refund ${formatCurrency(parseFloat(amount || 0))}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RefundModal;